import { ROUTER_PATH } from 'router/const/routerPath.const';
import { IUser } from 'modules/core/types';
import { SSO_TYPE, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { EXTERNAL_PATH } from 'router/const/externalPath.const';
import { Roles } from 'modules/core/enums';

import { defaultMenuLinks, IMenuLink, lenderTitleItem } from './sideBarMenuLinks';

export const getDealerSsoEffectiveLenderPath = (lenderPath: string | undefined): string | undefined => {
  const { pathname } = window.location;

  if (pathname.includes('/lenders/') || pathname === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE) {
    return pathname;
  }

  return lenderPath;
};

export const isDealerSsoModalLenderLink = (menuLink: IMenuLink): boolean => {
  if (!menuLink.link?.includes('/lenders/')) return false;

  if (isSbbPlusLink(menuLink) || isSbcPlusLink(menuLink)) return true;

  return !menuLink.isShopByBudgetEnabled && !(menuLink.isShopByCallbackEnabled && !menuLink.routeOneId);
};

export const filterDealerSsoNoInventoryMenuLinks = (menuLinks: IMenuLink[]): IMenuLink[] => {
  const sbbPlusLinks: IMenuLink[] = [];
  const sbcPlusLinks: IMenuLink[] = [];
  const generalLenders: IMenuLink[] = [];

  menuLinks.forEach((menuLink) => {
    if (!isDealerSsoModalLenderLink(menuLink)) return;

    if (isSbbPlusLink(menuLink)) {
      sbbPlusLinks.push(menuLink);
    } else if (isSbcPlusLink(menuLink)) {
      sbcPlusLinks.push(menuLink);
    } else {
      generalLenders.push(menuLink);
    }
  });

  const result = [...sbbPlusLinks, ...sbcPlusLinks];
  const sortedGeneralLenders = sortLenderLinks(generalLenders);

  if (sortedGeneralLenders.length > 0) {
    result.push(lenderTitleItem, ...sortedGeneralLenders);
  }

  return result;
};

export const getMakeItWorkForAllLendersLinks = (
  lenderLinks: IMenuLink[],
  disabledFeaturesList: string[],
  userRole?: IUser['role'],
): IMenuLink[] => {
  const enabledLenders = lenderLinks.filter((menuLink) => {
    const isFeatureDisabled = disabledFeaturesList.find((disabledFeature) => menuLink.featureName === disabledFeature);

    return !isFeatureDisabled;
  });

  const roleAwareLenders =
    userRole === Roles.SALESPERSON ? filterLinksBySalesPersonAccess(enabledLenders, true) : enabledLenders;

  return [...getShopByBudgetPlusLinks(roleAwareLenders), ...getShopByCallbackPlusLinks(roleAwareLenders)];
};

export const getSelectableLenderLinks = (
  lenderLinks: IMenuLink[],
  disabledFeaturesList: string[],
  userRole?: IUser['role'],
): IMenuLink[] => {
  const enabledLenders = filterByDisabledFeatures(lenderLinks, disabledFeaturesList);
  const roleAwareLenders =
    userRole === Roles.SALESPERSON ? filterLinksBySalesPersonAccess(enabledLenders, true) : enabledLenders;

  const generalLenders = roleAwareLenders.filter(
    (menuLink) => !menuLink.isShopByBudgetEnabled && !(menuLink.isShopByCallbackEnabled && !menuLink.routeOneId),
  );

  return sortLenderLinks(generalLenders);
};

export const sortLenderLinks = (links: IMenuLink[]): IMenuLink[] => {
  const lenderLinks = links.sort((a, b) => {
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;

    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;

    return 0;
  });

  // ['1','2','q','y','z'] => ['q','y','z','1','2']
  const fullLendersLink = lenderLinks
    .filter((lenderLink) => !Number(lenderLink.name[0]))
    .concat(lenderLinks.filter((lenderLink) => Number(lenderLink.name[0])));

  return fullLendersLink;
};

const prepareFullLendersLinks = (isRAC: boolean, lenderLinks: IMenuLink[], RACLink: IMenuLink): IMenuLink[] => {
  let lendersLinks: IMenuLink[] = isRAC ? lenderLinks.concat([RACLink]) : lenderLinks;

  lendersLinks = lendersLinks.filter(
    (menuLink) => !menuLink.isShopByBudgetEnabled && !(menuLink.isShopByCallbackEnabled && !menuLink.routeOneId),
  );

  const fullLendersLink = sortLenderLinks(lendersLinks);

  if (fullLendersLink.length > 0) fullLendersLink.unshift(lenderTitleItem);

  return fullLendersLink;
};

export const concatMenuLinks = (
  shopByBudgetPlusLinks: IMenuLink[],
  shopByCallbackPlusLinks: IMenuLink[],
  defaultMenuLinks: IMenuLink[],
  lenderLinks: IMenuLink[],
): IMenuLink[] => {
  const RACIndex = defaultMenuLinks.findIndex((item) => item.link === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE);
  const firstTailLinkIndex = defaultMenuLinks.findIndex(
    (item) =>
      item.link === ROUTER_PATH.CREDIT_INTERVIEW ||
      item.href === EXTERNAL_PATH.ADMIN_PANEL ||
      item.href === EXTERNAL_PATH.MANAGER_PANEL,
  );
  const isRAC: boolean = RACIndex >= 0;
  const separator: number = isRAC ? RACIndex : firstTailLinkIndex >= 0 ? firstTailLinkIndex : defaultMenuLinks.length;

  const firstPartMenuLinks = defaultMenuLinks.slice(0, separator);
  const lastPartMenuLinks = defaultMenuLinks.slice(isRAC ? separator + 1 : separator, defaultMenuLinks.length + 1);

  const RACLink = { ...defaultMenuLinks[RACIndex], smallFont: true };
  const fullLendersLink = prepareFullLendersLinks(isRAC, lenderLinks, RACLink);

  const shopByBudgetLink = firstPartMenuLinks.find((menuLink) => menuLink.link === ROUTER_PATH.PROBABILITY_ESTIMATOR);
  const shopByCallbackLink = firstPartMenuLinks.find((menuLink) => menuLink.link === ROUTER_PATH.APPROVED_LOAN_INPUT);

  const remainingFirstPartMenuLinks = firstPartMenuLinks.filter(
    (menuLink) => menuLink !== shopByBudgetLink && menuLink !== shopByCallbackLink,
  );

  return [
    ...shopByBudgetPlusLinks,
    ...(shopByBudgetLink ? [shopByBudgetLink] : []),
    ...shopByCallbackPlusLinks,
    ...(shopByCallbackLink ? [shopByCallbackLink] : []),
    ...remainingFirstPartMenuLinks,
    ...fullLendersLink,
    ...lastPartMenuLinks,
  ];
};

export const filterByDisabledFeatures = (menuLinks: IMenuLink[], disabledFeaturesList: string[]): IMenuLink[] =>
  menuLinks.filter((menuLink) => {
    const isFeatureDisabled = disabledFeaturesList.find((disabledFeature) => menuLink.featureName === disabledFeature);

    return (
      !isFeatureDisabled &&
      !menuLink.isShopByBudgetEnabled &&
      !(menuLink.isShopByCallbackEnabled && !menuLink.routeOneId)
    );
  });

export const getShopByBudgetPlusLinks = (menuLinks: IMenuLink[]): IMenuLink[] => {
  const links = JSON.parse(JSON.stringify(menuLinks)).filter((menuLink: IMenuLink) => isSbbPlusLink(menuLink));

  links.forEach((link: IMenuLink) => {
    link.iconUrl = STATIC_IMAGE_URLS.sideBar.probabilityEstimator;
    link.smallFont = false;
  });

  return links;
};

export const getShopByCallbackPlusLinks = (menuLinks: IMenuLink[]): IMenuLink[] => {
  const raw = JSON.parse(JSON.stringify(menuLinks)) as IMenuLink[];

  // '!menuLink.isShopByBudgetEnabled' is to prevent the duplicates (with SBB+ links) from being shown.
  const links = raw.filter((menuLink) => isSbcPlusLink(menuLink) && !menuLink.isShopByBudgetEnabled);

  links.forEach((link) => {
    link.iconUrl = STATIC_IMAGE_URLS.sideBar.approvedLoanInput;
    link.smallFont = false;
  });

  return links;
};

export const filterLinksBySalesPersonAccess = (menuLinks: IMenuLink[], IsSalesPersonAccess: boolean): IMenuLink[] =>
  menuLinks.filter((lenderLink) => lenderLink.isSalespersonAccess === IsSalesPersonAccess);

export const filterForSsoUser = (
  menuLinks: IMenuLink[],
  user: IUser | null,
  lenderPath: string | undefined = undefined,
): IMenuLink[] => {
  let ssoMenuLinks: IMenuLink[] = [];

  if (user?.is_sso) {
    if (user?.sso_type === SSO_TYPE.DEALER) {
      if (!user?.isWithInventory) {
        return filterDealerSsoNoInventoryMenuLinks(menuLinks);
      }

      return menuLinks.filter((menuLink) => !isAdminMenuLink(menuLink));
    }

    if (user?.isWithInventory) {
      menuLinks.forEach((menuLink) => {
        if (lenderPath && lenderPath === menuLink.link) {
          ssoMenuLinks.push(menuLink);
        } else if (
          !isAdminMenuLink(menuLink) &&
          (!menuLink.link?.includes('/lenders/') || isSbbPlusLink(menuLink) || isSbcPlusLink(menuLink)) &&
          !menuLink.link?.includes('/rac') &&
          menuLink.link !== ROUTER_PATH.CREDIT_INTERVIEW
        ) {
          ssoMenuLinks.push(menuLink);
        }
      });
    }

    if (!user?.isWithInventory) {
      menuLinks.forEach((menuLink) => {
        if (lenderPath && lenderPath === menuLink.link) {
          ssoMenuLinks.push(menuLink);
        }
      });

      if (
        lenderPath === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE &&
        !ssoMenuLinks.some((menuLink) => menuLink.link === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE)
      ) {
        const racMenuLink = defaultMenuLinks.find(
          (menuLink) => menuLink.link === ROUTER_PATH.RAC_AFFORDABILITY_ENGINE,
        ) as IMenuLink;

        ssoMenuLinks.push({
          ...racMenuLink,
          smallFont: true,
        });
      }
    }

    const isGeneralLenderLinksExists = ssoMenuLinks.some(
      (menuLink) => menuLink.link?.includes('/lenders/') && !isSbbPlusLink(menuLink) && !isSbcPlusLink(menuLink),
    );
    const isRacLinkExists = !!ssoMenuLinks.find((menuLink) => menuLink.link?.includes('/rac'));
    const isLenderTitleItemExists = !!ssoMenuLinks.find((menuLink) => menuLink.name === lenderTitleItem.name);

    if (isGeneralLenderLinksExists || isRacLinkExists) {
      if (!isLenderTitleItemExists) {
        user?.isWithInventory ? ssoMenuLinks.push(lenderTitleItem) : ssoMenuLinks.unshift(lenderTitleItem);
      }
    } else {
      ssoMenuLinks = ssoMenuLinks.filter((menuLink) => menuLink.name !== lenderTitleItem.name);
    }

    return ssoMenuLinks;
  }

  return menuLinks;
};

const isSbbPlusLink = (link: IMenuLink) => !!link.isShopByBudgetEnabled;
const isSbcPlusLink = (link: IMenuLink) => !!link.isShopByCallbackEnabled && !link.routeOneId;
const isAdminMenuLink = (link: IMenuLink): boolean => link.name.toLowerCase().includes('admin');
